"use client"

import { useState } from "react"
import Image from "next/image"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/Button"
import { Textarea } from "@/components/ui/textarea"

type FormStatus = 'idle' | 'loading' | 'success' | 'error'

export default function ContactPage() {
    const [formState, setFormState] = useState({
        name: "",
        email: "",
        subject: "Order Inquiry",
        message: ""
    })
    const [status, setStatus] = useState<FormStatus>('idle')
    const [errorMessage, setErrorMessage] = useState('')

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setStatus('loading')
        setErrorMessage('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formState),
            })

            const data = await response.json()

            if (!response.ok) {
                throw new Error(data.error || 'Failed to send message')
            }

            setStatus('success')
            setFormState({
                name: "",
                email: "",
                subject: "Order Inquiry",
                message: ""
            })
        } catch (error) {
            setStatus('error')
            setErrorMessage(error instanceof Error ? error.message : 'An unexpected error occurred')
        }
    }

    const inquiryTypes = ['Order Inquiry', 'Technical Data', 'Wholesale', 'Other']

    return (
        <div className="bg-white min-h-screen pb-20">
            {/* Hero Section */}
            <section className="relative w-full overflow-hidden border-b border-slate-100 bg-slate-100">
                <div className="absolute inset-0">
                    <Image
                        src="/contact-hero.webp"
                        alt=""
                        fill
                        priority
                        sizes="100vw"
                        className="object-cover object-right"
                    />
                    {/* Left-to-right light scrim so the dark headline stays legible.
                        Stronger on mobile where the text overlaps the photo. */}
                    <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/40 md:via-white/80 md:to-transparent" />
                </div>
                <div className="relative mx-auto max-w-6xl px-6 py-20 md:py-28">
                    <div className="mb-4 flex items-center gap-3">
                        <span className="h-px w-8 bg-[#008080]" />
                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#008080]">
                            Get in Touch
                        </span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">
                        Contact Us
                    </h1>
                    <p className="mt-4 text-lg text-slate-700 max-w-2xl font-medium">
                        Have a question about an order or need technical information? We typically respond within 2 hours during business hours.
                    </p>
                </div>
            </section>

            {/* Main Content */}
            <section className="max-w-5xl mx-auto px-6 py-12">
                <div className="grid lg:grid-cols-3 gap-10">
                    {/* Contact Form */}
                    <div className="lg:col-span-2">
                        <h2 className="text-2xl font-bold text-slate-900 mb-6">Send us a message</h2>
                        <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-slate-700">Name</label>
                                        <Input
                                            type="text"
                                            required
                                            value={formState.name}
                                            onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                                            placeholder="Your name"
                                            className="bg-slate-50 focus:bg-white"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-slate-700">Email</label>
                                        <Input
                                            type="email"
                                            required
                                            value={formState.email}
                                            onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                                            placeholder="you@email.com"
                                            className="bg-slate-50 focus:bg-white"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-slate-700">What can we help with?</label>
                                    <div className="flex flex-wrap gap-2">
                                        {inquiryTypes.map((type) => (
                                            <button
                                                key={type}
                                                type="button"
                                                onClick={() => setFormState({ ...formState, subject: type })}
                                                className={`px-4 py-2 text-sm font-medium rounded-full border transition-all ${formState.subject === type
                                                    ? "bg-teal-800 text-white border-teal-800"
                                                    : "bg-white text-slate-600 border-slate-200 hover:border-teal-300 hover:text-teal-800"
                                                    }`}
                                            >
                                                {type}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-slate-700">Message</label>
                                    <Textarea
                                        required
                                        value={formState.message}
                                        onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                                        className="min-h-[140px] bg-slate-50 focus:bg-white resize-y"
                                        placeholder="Tell us more about your inquiry..."
                                    />
                                </div>

                                {/* Success Message */}
                                {status === 'success' && (
                                    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                                        <p className="font-medium text-green-800">Message sent!</p>
                                        <p className="text-green-700 text-sm mt-1">We'll get back to you as soon as possible.</p>
                                    </div>
                                )}

                                {/* Error Message */}
                                {status === 'error' && (
                                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                        <p className="font-medium text-red-800">Failed to send</p>
                                        <p className="text-red-700 text-sm mt-1">{errorMessage || 'Please try again or email us directly.'}</p>
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    disabled={status === 'loading'}
                                    size="lg"
                                    className="w-full md:w-auto px-8"
                                >
                                    {status === 'loading' ? 'Sending...' : 'Send Message'}
                                </Button>
                            </form>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        <h2 className="text-2xl font-bold text-slate-900">Other ways to reach us</h2>

                        {/* Direct Email Card */}
                        <div className="bg-teal-50 rounded-2xl p-6 border border-teal-100">
                            <h3 className="text-sm font-semibold text-slate-900 mb-2">Email Support</h3>
                            <a
                                href="mailto:support@fastpeps.com"
                                className="text-teal-800 font-medium hover:underline text-lg"
                            >
                                support@fastpeps.com
                            </a>
                            <p className="text-sm text-slate-600 mt-3">
                                Response within 2 hours<br />
                                Mon-Fri, 9am-6pm EST
                            </p>
                        </div>

                        {/* Phone Card */}
                        <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                            <h3 className="text-sm font-semibold text-slate-900 mb-2">Phone</h3>
                            <a
                                href="tel:228-200-3390"
                                className="text-teal-800 font-medium hover:underline text-lg"
                            >
                                228-200-3390
                            </a>
                            <p className="text-sm text-slate-600 mt-3">
                                Mon-Fri, 9am-6pm EST
                            </p>
                        </div>

                        {/* Address Card */}
                        <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                            <h3 className="text-sm font-semibold text-slate-900 mb-2">Mailing Address</h3>
                            <p className="text-slate-700 text-sm leading-relaxed">
                                FastPeps LLC<br />
                                270 Trace Colony Park Suite B<br />
                                Ridgeland, MS 39157
                            </p>
                        </div>

                        {/* Community Section */}
                        <div className="relative overflow-hidden rounded-2xl shadow-md">
                            <Image
                                src="/community-bg.webp"
                                alt=""
                                fill
                                loading="lazy"
                                className="object-cover object-center"
                                sizes="400px"
                                quality={60}
                            />
                            <div className="absolute inset-0 bg-slate-900/80" />
                            <div className="relative z-10 p-6 text-center">
                                <h3 className="text-lg font-bold text-white mb-2">
                                    Join the Research Community
                                </h3>
                                <p className="text-slate-300 text-sm mb-5">
                                    Connect with fellow researchers and get real-time updates.
                                </p>
                                <div className="flex flex-col gap-3">
                                    <a
                                        href="#"
                                        className="flex items-center justify-center gap-2 px-4 py-3 bg-[#5865F2] hover:bg-[#4752C4] text-white rounded-xl font-semibold transition-all duration-200 text-sm"
                                    >
                                        <svg viewBox="0 0 127.14 96.36" className="w-4 h-4 fill-current">
                                            <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.11,77.11,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22c1.24-23.25-13.23-47.57-18.9-72.15ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z" />
                                        </svg>
                                        Join Discord
                                    </a>
                                    <a
                                        href="#"
                                        className="flex items-center justify-center gap-2 px-4 py-3 bg-[#229ED9] hover:bg-[#1A8AC7] text-white rounded-xl font-semibold transition-all duration-200 text-sm"
                                    >
                                        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                                            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                                        </svg>
                                        Join Telegram
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* FAQ Link */}
                        <LocalizedClientLink href="/faq" className="block bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all group">
                            <h3 className="text-sm font-semibold text-slate-900 mb-2 group-hover:text-teal-800 transition-colors">Browse FAQ</h3>
                            <p className="text-sm text-slate-500">
                                Instant answers to common questions about orders, shipping, and quality.
                            </p>
                        </LocalizedClientLink>
                    </div>
                </div>
            </section>
        </div>
    )
}
